// Curated palette library. Each palette has a stable id used in the DSL —
// renaming an id is a breaking change and requires a migration entry.

import { parseHex, type RGB } from '@/lib/color';

export type PaletteDef = {
  id: string;
  name: string;
  colors: string[]; // hex
};

export const PALETTES: readonly PaletteDef[] = [
  {
    id: 'mono',
    name: 'Mono (1-bit)',
    colors: ['#000000', '#ffffff'],
  },
  {
    id: 'gb-original',
    name: 'GB Original',
    colors: ['#0f380f', '#306230', '#8bac0f', '#9bbc0f'],
  },
  {
    id: 'gb-pocket',
    name: 'GB Pocket',
    colors: ['#181818', '#5d6d5e', '#a8b3a8', '#e6e6e6'],
  },
  {
    id: 'cga-0',
    name: 'CGA 0',
    colors: ['#000000', '#00aa00', '#aa0000', '#aa5500'],
  },
  {
    id: 'cga-1',
    name: 'CGA 1',
    colors: ['#000000', '#55ffff', '#ff55ff', '#ffffff'],
  },
  {
    id: 'lava-gb',
    name: 'Lava GB',
    colors: ['#051f39', '#4a2480', '#c53a9d', '#ff8e80'],
  },
  {
    id: 'kirokaze-gb',
    name: 'Kirokaze GB',
    colors: ['#332c50', '#46878f', '#94e344', '#e2f3e4'],
  },
  {
    id: 'mist-gb',
    name: 'Mist GB',
    colors: ['#2d1b00', '#1e606e', '#5ab9a8', '#c4f0c2'],
  },
  {
    id: 'ice-cream-gb',
    name: 'Ice Cream GB',
    colors: ['#7c3f58', '#eb6b6f', '#f9a875', '#fff6d3'],
  },
  {
    id: 'twilight-5',
    name: 'Twilight 5',
    colors: ['#fbbbad', '#ee8695', '#4a7a96', '#333f58', '#292831'],
  },
  {
    id: 'oil-6',
    name: 'Oil 6',
    colors: ['#fbf5ef', '#f2d3ab', '#c69fa5', '#8b6d9c', '#494d7e', '#272744'],
  },
  {
    id: 'duotone-orange',
    name: 'Duotone Orange',
    colors: ['#1a0a05', '#7a2d10', '#ff5a1f', '#ffffff'],
  },
  {
    id: 'ammo-8',
    name: 'Ammo 8',
    colors: ['#040c06', '#112318', '#1e3a29', '#305d42', '#4d8061', '#89a257', '#bedc7f', '#eeffcc'],
  },
  {
    id: 'rust-gold-8',
    name: 'Rust Gold 8',
    colors: ['#202020', '#393939', '#725956', '#bb7f57', '#331c17', '#563226', '#ac6b26', '#f6cd26'],
  },
  {
    id: 'pollen-8',
    name: 'Pollen 8',
    colors: ['#73464c', '#ab5675', '#ee6a7c', '#ffa7a5', '#ffe07e', '#ffe7d6', '#72dcbb', '#34acba'],
  },
  {
    id: 'arq4-8',
    name: 'ARQ4 8',
    colors: ['#ffffff', '#6df7c1', '#11adc1', '#606c81', '#393457', '#1e8875', '#5bb361', '#a1e55a'],
  },
  {
    id: 'sweetie-16',
    name: 'Sweetie 16',
    colors: [
      '#1a1c2c', '#5d275d', '#b13e53', '#ef7d57', '#ffcd75', '#a7f070',
      '#38b764', '#257179', '#29366f', '#3b5dc9', '#41a6f6', '#73eff7',
      '#f4f4f4', '#94b0c2', '#566c86', '#333c57',
    ],
  },
  {
    id: 'pico-8',
    name: 'PICO-8',
    colors: [
      '#000000', '#1d2b53', '#7e2553', '#008751', '#ab5236', '#5f574f',
      '#c2c3c7', '#fff1e8', '#ff004d', '#ffa300', '#ffec27', '#00e436',
      '#29adff', '#83769c', '#ff77a8', '#ffccaa',
    ],
  },
  {
    id: 'dawnbringer-16',
    name: 'DawnBringer 16',
    colors: [
      '#140c1c', '#442434', '#30346d', '#4e4a4f', '#854c30', '#346524',
      '#d04648', '#757161', '#597dce', '#d27d2c', '#8595a1', '#6daa2c',
      '#d2aa99', '#6dc2ca', '#dad45e', '#deeed6',
    ],
  },
  {
    id: 'endesga-32',
    name: 'Endesga 32',
    colors: [
      '#be4a2f', '#d77643', '#ead4aa', '#e4a672', '#b86f50', '#733e39',
      '#3e2731', '#a22633', '#e43b44', '#f77622', '#feae34', '#fee761',
      '#63c74d', '#3e8948', '#265c42', '#193c3e', '#124e89', '#0099db',
      '#2ce8f5', '#ffffff', '#c0cbdc', '#8b9bb4', '#5a6988', '#3a4466',
      '#262b44', '#181425', '#ff0044', '#68386c', '#b55088', '#f6757a',
      '#e8b796', '#c28569',
    ],
  },
];

export const DEFAULT_PALETTE_ID = 'sweetie-16';

export function getPalette(id: string): PaletteDef | undefined {
  return PALETTES.find((p) => p.id === id);
}

export function paletteToRGB(p: PaletteDef): RGB[] {
  return p.colors.map((c) => parseHex(c) ?? ([0, 0, 0] as RGB));
}

export function paletteIds(): string[] {
  return PALETTES.map((p) => p.id);
}
